import React, { Component, ReactNode } from 'react';
import './SignupEmail.scss';
import { get } from '../../../services/BackendService';
import { EMAIL_PATTERN } from '../../../utils/Validations';
import { NotificationBanner } from '../../shared/NotificationBanner/NotificationBanner';
import { Routes, SandboxRoutes } from '../../../app/AppRouter';
import { ErrorCode } from '../../../utils/ErrorCode';
import { AUTH_API_URL, config } from '../../../app.config';
import { useDataswiftNotification } from '../../../utils/customHooks';
import { Link } from 'react-router-dom';

const debounce = require('lodash.debounce');

const ERROR_MESSAGES: { [key: string]: string } = {
  emailAlreadyExists: 'A HAT with this email already exists.',
  emailInvalid: 'Email is invalid',
};

class SignupEmailView extends Component<IEmailCreateProps, IEmailCreateState> {
  state = {
    email: '',
    subscriptions: false,
    validEmail: false,
    notAvailableEmail: false,
    errorType: '',
  };

  validateEmailDebounce = debounce((email: string) => this.validateEmail(email), 200);

  async validateEmail(email: string) {
    try {
      const res = await get(
        `${AUTH_API_URL}/api/validate-email?email=${encodeURIComponent(email)}&sandbox=${this.props.sandbox}`,
      );
      if (res.parsedBody) {
        this.setState({
          notAvailableEmail: false,
        });

        this.props.emailNext(this.state.email, this.state.subscriptions);
      }
    } catch (error) {
      if (error.value) {
        const hattersError = ErrorCode[error.value];

        if (hattersError.errorCode === 11) {
          this.setState({
            errorType: 'emailAlreadyExists',
            notAvailableEmail: true,
          });
        } else {
          this.setState({
            errorType: 'emailInvalid',
            notAvailableEmail: true,
          });
        }
      }
    }
  }

  onEmailChange(email: string) {
    const validEmail = EMAIL_PATTERN.test(email);
    this.setState({
      email: email,
      validEmail: validEmail,
    });
  }

  onCheckboxChange(value: boolean) {
    this.setState({
      subscriptions: value,
    });
  }

  inputFocus = () => {
    this.setState({
      notAvailableEmail: false,
      errorType: '',
    });
  };

  onNextClicked = () => {
    if (this.state.validEmail) {
      this.validateEmailDebounce(this.state.email.toLowerCase());
    }
  };

  errorText(): string {
    return ERROR_MESSAGES[this.state.errorType] || '';
  }

  render(): ReactNode {
    return (
      <div className="hatters-flex-column-container">
        {this.props.ribbon}
        {this.state.notAvailableEmail && <NotificationBanner type={'error'} message={this.errorText()} />}

        <span className={'flex-spacer-small'} />

        <div className="email-wrapper">
          <div className="email-title-container">
            <div className="hatters-title">Enter your email</div>
          </div>

          <div className="hatters-description-normal">
            This information is only used to create your HAT Personal Data Account (PDA). Your personal information will
            never be shared with anyone unless you authorise it.
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              this.onNextClicked();
            }}
          >
            <input
              className="hatters-input"
              style={this.state.notAvailableEmail ? { borderColor: '#E45151' } : {}}
              name={'email'}
              type={'email'}
              placeholder="Email Address"
              autoComplete={'email'}
              onFocus={this.inputFocus}
              onChange={(e) => this.onEmailChange(e.target.value)}
            />
          </form>
          {this.props.sandbox ? (
            <>
              <div className="hatters-description-small">
                Learn how we treat your information with respect in our
                <a href={config.links.privacyPolicy} target="_blank" rel="noopener noreferrer">
                  &nbsp;privacy policy
                </a>
                . By clicking ‘Next’ you agree to have read the privacy policy.
              </div>
            </>
          ) : (
            <>
              <div className={'checkbox-container'}>
                <label htmlFor={'cbSubs'}>
                  Subscribe to MadHATTERs (Tech news, HAT slanted)
                  <input
                    id={'cbSubs'}
                    name={'cbSubs'}
                    type={'checkbox'}
                    checked={this.state.subscriptions}
                    onChange={(e) => this.onCheckboxChange(e.target.checked)}
                  />
                  <span className="checkbox-checkmark" />
                </label>
              </div>

              <div className="hatters-description-small">
                You can change your mind at any time by clicking the unsubscribe link in the footer of any email you
                receive from us. Learn how we treat your information with respect in our
                <a href={config.links.privacyPolicy} target="_blank" rel="noopener noreferrer">
                  &nbsp;privacy policy
                </a>
                . By clicking ‘Next’ you agree to have read the privacy policy.
              </div>
            </>
          )}

          <div className="email-user-actions">
            <button
              className="hatters-btn hatters-btn-accent"
              disabled={!this.state.validEmail}
              onClick={() => this.onNextClicked()}
            >
              Next
            </button>
            <div className="hatters-description-normal">
              Have an account?
              <Link to={this.props.sandbox ? SandboxRoutes.hatLogin : Routes.hatLogin}>Log in</Link>{' '}
            </div>
          </div>
        </div>
        <span className={'flex-spacer-large'} />
      </div>
    );
  }
}

const withRibbon = (Component: typeof SignupEmailView) => {
  const WithRibbon = (props: IEmailCreateProps) => {
    const ribbon = useDataswiftNotification(true);
    return <Component ribbon={ribbon} {...props} />;
  };

  return WithRibbon;
};

export const SignupEmail = withRibbon(SignupEmailView);

interface IEmailCreateState {
  email: string;
  subscriptions: boolean;
  validEmail: boolean;
  notAvailableEmail: boolean;
  errorType: string;
}

interface IEmailCreateProps {
  emailNext: Function;
  cancelClicked: Function;
  sandbox: boolean;
  ribbon?: React.ReactElement;
}
